import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

function Countdown() {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [eventStarted, setEventStarted] = useState(false);

  useEffect(() => {
    const eventDate = new Date("2026-08-07T20:00:00").getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = eventDate - now;

      if (distance < 0) {
        setEventStarted(true);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown(); // Initial call
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, []);

  const timeBoxes = [
    { value: timeLeft.days, label: t("countdown.days") },
    { value: timeLeft.hours, label: t("countdown.hours") },
    { value: timeLeft.minutes, label: t("countdown.minutes") },
    { value: timeLeft.seconds, label: t("countdown.seconds") },
  ];

  return (
    <section className="pt-12 pb-12 bg-slate-50/50 dark:bg-slate-900 transition-colors relative z-10" id="countdown">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("countdown.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
            {t("countdown.description")}
          </p>
        </div>

        {/* Countdown Timer */}
        {eventStarted ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
              <i className="fas fa-check-circle text-green-600 dark:text-green-400 text-3xl"></i>
            </div>
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {t("countdown.event_started")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row md:justify-center md:items-center gap-3 md:gap-4 lg:gap-6">
            {timeBoxes.map((box, index) => (
              <React.Fragment key={index}>
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 md:p-6 lg:p-8 text-center hover:shadow-md transition-all duration-300 hover:-translate-y-1 w-full max-w-[200px] md:max-w-none md:w-auto mx-auto md:mx-0">
                  <span className="block text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-primary dark:text-primary-light mb-1 md:mb-2 drop-shadow-sm">
                    {String(box.value).padStart(2, "0")}
                  </span>
                  <p className="text-xs md:text-sm lg:text-base text-slate-600 dark:text-slate-400 uppercase tracking-wider font-medium">
                    {box.label}
                  </p>
                </div>
                {index < timeBoxes.length - 1 && (
                  <span className="hidden md:inline-block text-3xl md:text-4xl font-bold text-primary dark:text-primary-light countdown-separator self-center">
                    :
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Animation Styles */}
        <style>{`
          @keyframes countdown-pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.4;
            }
          }

          .countdown-separator {
            animation: countdown-pulse 2s infinite;
          }
        `}</style>
      </div>
    </section>
  );
}

export default Countdown;
